// ============================================================================
// Confidence tiers — ONE definition, shared by every surface that shows a
// sourced price (DraftReviewCard for whole drafts, ProposalCard for single
// line-item cards).
// ----------------------------------------------------------------------------
// Tiers are computed SERVER-SIDE by pq-chat against the real source row and
// are never written by the model (docs/pq_draft_proposal_spec.md §2). This
// module only renders what the server decided.
//
// Letter AND colour on every badge, always: colour alone fails on a phone in
// direct sunlight and fails for colour-blind users, and this gets read at
// jobsites.
// ============================================================================
import React, { useState } from 'react';
import { cn } from '@/lib/utils';

export const BASIS_TIERS = {
  A: {
    letter: 'A',
    label: 'Sourced',
    cls: 'bg-green-500/15 text-green-600 border-green-500/40',
  },
  B: {
    letter: 'B',
    label: 'Stale',
    cls: 'bg-amber-500/15 text-amber-600 border-amber-500/40',
  },
  C: {
    letter: 'C',
    label: 'Thin',
    cls: 'bg-orange-500/15 text-orange-600 border-orange-500/40',
  },
  Y: {
    letter: 'Y',
    label: 'Your figure',
    cls: 'bg-sky-500/15 text-sky-600 border-sky-500/40',
  },
};

/** One line of plain-text provenance: tier, store, date, prior job. */
export const basisSummary = (basis) => {
  if (!basis) return '';
  const tier = BASIS_TIERS[basis.tier];
  return [
    tier ? tier.label : basis.tier,
    basis.store || null,
    basis.captured || null,
    basis.job ? `from ${basis.job}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Tap-to-expand confidence badge. Expands in place rather than in a popover —
 * the chat panel is ~400px wide, where popovers are miserable at any screen
 * size. Renders nothing when there's no graded basis (an unpriced line shows
 * an empty field instead, which announces itself; a grey badge gets skimmed).
 */
export const BasisBadge = ({ basis, className }) => {
  const [open, setOpen] = useState(false);
  const tier = basis ? BASIS_TIERS[basis.tier] : null;
  if (!tier) return null;

  return (
    <span className={cn('inline-flex flex-col items-start gap-1', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={tier.label}
        aria-expanded={open}
        className={cn(
          'h-4 w-4 shrink-0 rounded border text-[9px] font-bold leading-none',
          'inline-flex items-center justify-center',
          tier.cls
        )}
        data-testid={`pq-basis-badge-${basis.tier}`}
      >
        {tier.letter}
      </button>
      {open ? (
        <span className="block border-l-2 border-primary/25 pl-2 text-[11px] text-muted-foreground">
          <span className="block">{basisSummary(basis)}</span>
          {basis.detail ? <span className="block">{basis.detail}</span> : null}
          {basis.match && basis.match !== 'exact' ? (
            <span className="block italic">Match: {basis.match}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
};

export default BasisBadge;
